"use client";

import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { UNSAFE_PortalProvider } from "react-aria";
import { RouterProvider } from "react-aria-components";

import { applyKitBrand, kitBrandProperties, type KitBrand } from "./_logic/brand";
import { NOWADAYS_UI_SCOPE_CLASS } from "./_logic/constants";
import { cn } from "./lib/utils";

interface NowadaysUiScopeProps {
  children: React.ReactNode;
  /** Classes for the wrapping element (a background, a min height). */
  className?: string;
  /**
   * Colors to lay over the kit's `primary`, `primary-foreground` and `ring`, such as an event
   * org's brand on a guest form. Hex only; a brand with any other value is ignored. Null or left
   * out keeps the Nowadays colors.
   */
  brand?: KitBrand | null;
}

/**
 * Turns on the Nowadays design tokens for a screen built from `@/nowadays-ui`. Inside it,
 * `primary`, the radius scale, the easing curves and the base border color take the venues
 * values instead of daisyUI's and Tailwind's (see `styles/theme.css`).
 *
 * React Aria renders menus, popovers and dialogs into `document.body`, outside this wrapper, so
 * it also gives them a container of their own on `body` that carries the scope class. Until that
 * container mounts (the first client render), overlays fall back to `body`. And it hands React
 * Aria the Next.js router, so `LinkButton` and other React Aria links navigate client-side instead
 * of reloading the page.
 *
 * With a `brand`, it writes the brand's colors as custom properties on the wrapper (in render, so
 * the first paint is already branded) and on that overlay container, since `--color-primary`
 * resolves on the scope element itself and overlays sit outside the wrapper.
 * @param props - See {@link NowadaysUiScopeProps}.
 * @returns The scoped wrapper.
 */
export function NowadaysUiScope({ children, className, brand }: NowadaysUiScopeProps): React.ReactElement {
  // Hooks
  const router = useRouter();

  // States
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  // Derived values
  // Keyed on the three strings, so a caller passing a fresh object each render does not rewrite them.
  const primary = brand?.primary;
  const primaryForeground = brand?.primaryForeground;
  const ring = brand?.ring;
  const brandProperties = useMemo(
    () =>
      primary === undefined || primaryForeground === undefined
        ? null
        : kitBrandProperties({ primary, primaryForeground, ring }),
    [primary, primaryForeground, ring]
  );

  // Effects
  useEffect(() => {
    const container = document.createElement("div");
    container.className = NOWADAYS_UI_SCOPE_CLASS;
    document.body.appendChild(container);
    setPortalContainer(container);
    return () => container.remove();
  }, []);

  useEffect(() => {
    if (portalContainer) applyKitBrand(portalContainer, brandProperties);
  }, [portalContainer, brandProperties]);

  // Render
  return (
    <div className={cn(NOWADAYS_UI_SCOPE_CLASS, className)} style={brandProperties ?? undefined}>
      <RouterProvider navigate={router.push}>
        <UNSAFE_PortalProvider getContainer={() => portalContainer}>{children}</UNSAFE_PortalProvider>
      </RouterProvider>
    </div>
  );
}
